import { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../../context/LanguageContext';
import { useConfig } from '../../context/ConfigContext';
import { PARTS, CATEGORIES, BASE_PRICE } from '../../data/parts';
import * as api from '../../services/api';
import styles from './OfficialUpload.module.css';

export default function OfficialUpload() {
    const navigate = useNavigate();
    const { t } = useLanguage();
    const { exportConfig } = useConfig();
    const fileInputRef = useRef(null);

    const [images, setImages] = useState([]);
    const [selectedConfig, setSelectedConfig] = useState({
        color: 'm04',
        wheels: 'standard_wheels',
        exhaust: 'stock_exhaust',
        seat: 'solo_seat',
        accessories: [],
    });
    const [formData, setFormData] = useState({
        title: '',
        description: '',
    });
    const [error, setError] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [success, setSuccess] = useState(false);

    // Calculate total price
    const totalPrice = (() => {
        let total = BASE_PRICE;
        Object.entries(selectedConfig).forEach(([category, value]) => {
            if (Array.isArray(value)) {
                value.forEach(itemId => {
                    const part = PARTS[category]?.find(p => p.id === itemId);
                    if (part) total += part.price;
                });
            } else {
                const part = PARTS[category]?.find(p => p.id === value);
                if (part) total += part.price;
            }
        });
        return total;
    })();

    const handleFileSelect = (e) => {
        const files = Array.from(e.target.files);
        const maxImages = 10;
        const remainingSlots = maxImages - images.length;

        if (files.length > remainingSlots) {
            setError(`Maximum ${maxImages} images allowed.`);
            return;
        }

        const validFiles = files.filter(file => {
            if (!file.type.startsWith('image/')) {
                setError('Only image files are allowed');
                return false;
            }
            if (file.size > 10 * 1024 * 1024) {
                setError('Each image must be less than 10MB');
                return false;
            }
            return true;
        });

        const newImages = validFiles.map(file => ({
            file,
            preview: URL.createObjectURL(file),
            id: Math.random().toString(36).substr(2, 9),
        }));

        setImages(prev => [...prev, ...newImages]);
        setError('');
    };

    const removeImage = (id) => {
        setImages(prev => {
            const img = prev.find(i => i.id === id);
            if (img) URL.revokeObjectURL(img.preview);
            return prev.filter(i => i.id !== id);
        });
    };

    const updateConfig = (category, value) => {
        setSelectedConfig(prev => {
            if (category === 'accessories') {
                const current = prev.accessories || [];
                const exists = current.includes(value);
                return {
                    ...prev,
                    accessories: exists
                        ? current.filter(id => id !== value)
                        : [...current, value],
                };
            }
            return { ...prev, [category]: value };
        });
    };

    const useCurrentConfig = () => {
        if (exportConfig) {
            setSelectedConfig(exportConfig());
        }
    };

    const handleSubmit = async () => {
        setError('');

        if (images.length === 0) {
            setError('Please upload at least one image');
            return;
        }

        if (!formData.title.trim()) {
            setError('Please enter a title');
            return;
        }

        setSubmitting(true);

        try {
            // Create official configuration
            const createResult = await api.createOfficialConfiguration({
                title: formData.title.trim(),
                description: formData.description.trim(),
                config_data: selectedConfig,
                total_price: totalPrice,
                is_official: true,
            });

            if (!createResult.success) {
                throw new Error(createResult.error || 'Failed to create configuration');
            }

            const configId = createResult.data.id;

            // Upload images
            const imageFiles = images.map(img => img.file);
            await api.uploadConfigurationImages(configId, imageFiles);

            setSuccess(true);
            setTimeout(() => {
                navigate('/admin');
            }, 2000);

        } catch (err) {
            console.error('Submit error:', err);
            setError(err.message || 'Failed to create official configuration');
        } finally {
            setSubmitting(false);
        }
    };

    if (success) {
        return (
            <div className={styles.successMessage}>
                <div className={styles.successIcon}>✓</div>
                <h2>{t('admin.official.success') || 'Official Configuration Created!'}</h2>
                <p>{t('admin.official.redirecting') || 'Redirecting to dashboard...'}</p>
            </div>
        );
    }

    return (
        <div className={styles.officialUpload}>
            <h1 className={styles.title}>
                {t('admin.official.title') || 'Create Official Configuration'}
            </h1>

            {error && <div className={styles.error}>{error}</div>}

            <div className={styles.form}>
                {/* Images Section */}
                <section className={styles.section}>
                    <h2>{t('admin.official.images') || 'Images'}</h2>
                    <div className={styles.imageGrid}>
                        {images.map((img, index) => (
                            <div key={img.id} className={styles.imageCard}>
                                <img src={img.preview} alt="" />
                                {index === 0 && <span className={styles.coverBadge}>Cover</span>}
                                <button
                                    className={styles.removeBtn}
                                    onClick={() => removeImage(img.id)}
                                >
                                    ×
                                </button>
                            </div>
                        ))}
                        {images.length < 10 && (
                            <button
                                className={styles.addImageBtn}
                                onClick={() => fileInputRef.current?.click()}
                            >
                                <span>+</span>
                                <span>Add Image</span>
                            </button>
                        )}
                    </div>
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*"
                        multiple
                        onChange={handleFileSelect}
                        style={{ display: 'none' }}
                    />
                </section>

                {/* Configuration Section */}
                <section className={styles.section}>
                    <div className={styles.sectionHeader}>
                        <h2>{t('admin.official.configuration') || 'Configuration'}</h2>
                        <button className={styles.useCurrentBtn} onClick={useCurrentConfig}>
                            {t('admin.official.useCurrent') || 'Use Current Settings'}
                        </button>
                    </div>

                    <div className={styles.configPicker}>
                        {CATEGORIES.map(category => (
                            <div key={category.id} className={styles.categorySection}>
                                <h3>{t(category.nameKey) || category.id}</h3>
                                <div className={styles.partOptions}>
                                    {PARTS[category.id]?.map(part => {
                                        const isSelected = category.id === 'accessories'
                                            ? selectedConfig.accessories?.includes(part.id)
                                            : selectedConfig[category.id] === part.id;

                                        return (
                                            <button
                                                key={part.id}
                                                className={`${styles.partOption} ${isSelected ? styles.selected : ''}`}
                                                onClick={() => updateConfig(category.id, part.id)}
                                            >
                                                {part.colorCode && (
                                                    <span
                                                        className={styles.colorSwatch}
                                                        style={{ background: part.colorCode }}
                                                    />
                                                )}
                                                <span className={styles.partName}>
                                                    {t(part.nameKey) || part.id}
                                                </span>
                                                {part.price > 0 && (
                                                    <span className={styles.partPrice}>
                                                        +¥{part.price.toLocaleString()}
                                                    </span>
                                                )}
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>
                        ))}
                    </div>

                    <div className={styles.totalSection}>
                        <span>Total Price</span>
                        <span className={styles.totalPrice}>¥{totalPrice.toLocaleString()}</span>
                    </div>
                </section>

                {/* Details Section */}
                <section className={styles.section}>
                    <h2>{t('admin.official.details') || 'Details'}</h2>

                    <div className={styles.formGroup}>
                        <label>{t('admin.official.titleLabel') || 'Title'} *</label>
                        <input
                            type="text"
                            value={formData.title}
                            onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                            placeholder="e.g., 2026 Fat Boy Classic Black"
                            maxLength={100}
                        />
                    </div>

                    <div className={styles.formGroup}>
                        <label>{t('admin.official.descriptionLabel') || 'Description'}</label>
                        <textarea
                            value={formData.description}
                            onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                            placeholder="Describe this official configuration..."
                            rows={4}
                            maxLength={2000}
                        />
                    </div>
                </section>

                {/* Submit */}
                <div className={styles.actions}>
                    <button
                        className={styles.cancelBtn}
                        onClick={() => navigate('/admin')}
                    >
                        {t('common.cancel') || 'Cancel'}
                    </button>
                    <button
                        className={styles.submitBtn}
                        onClick={handleSubmit}
                        disabled={submitting || !formData.title.trim() || images.length === 0}
                    >
                        {submitting
                            ? (t('common.submitting') || 'Creating...')
                            : (t('admin.official.create') || 'Create Official Config')
                        }
                    </button>
                </div>
            </div>
        </div>
    );
}
